import {
    IExpressionResolver, IExpressionResolution, failedExpressionResolution, ResolutionErrorKind,
    successfulExpressionResolution, IResolutionError
} from 'app/model/expressions/expression-resolution';
import { Metamodel } from 'app/model/base/model';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { ForEachActivity } from 'app/model/pipeline/foreach.activity';
import { ScalarExpression, ExpressionCategory } from 'app/model/expressions/expression';
import { getPropertyStack, matchPropertyStackWithSchema, buildSubExpressionResolver } from './expression-utils';
import { Owned } from '../base/named';
import { FilterActivity } from 'app/model/pipeline/filter.activity';
import { numberOfLeadingWhitespace } from 'app/shared/utils/string.util';

/**
 * Resolves @items expressions used inside filter and foreach sub activities.
 */
export class ItemResolver implements IExpressionResolver {
    /**
     * Initializes a new instance of the ForeachItemResolver class.
     * @param _entity The entity owning the field being parameterized.
     * @param _expressionResolversCallback Provides supported expression resolvers used to resolve function arguments.
     */
    public constructor(private _entity: Owned, private readonly _expressionResolversCallback: () => IExpressionResolver[]) {
    }

    public isOfType(expression: string, ignoreStart?: boolean): boolean {
        expression = expression ? expression.trim() : '';

        return Boolean(expression) && expression.toLowerCase().startsWith(this.getPrefix(ignoreStart));
    }

    public resolve(expression: string, ignoreStart?: boolean): IExpressionResolution {
        if (!this.isOfType(expression, ignoreStart)) {
            return failedExpressionResolution(expression, { kind: ResolutionErrorKind.Unrecognized, details : expression });
        }

        expression = expression ? expression.trimRight() : '';

        // skip the prefix and leading whitespace.
        const offset = this.getPrefix(ignoreStart).length + numberOfLeadingWhitespace(expression);
        const syntax = expression.substring(offset);

        // get the nested properties
        let propertyNestings: Array<{ property: string, separator: string }>;

        try {
            // ensure that any nested properties after @item() are properly split by dots
            propertyNestings = getPropertyStack(syntax, offset);
        } catch (parsingError) {
            return failedExpressionResolution(syntax, {
                kind: ResolutionErrorKind.InvalidSyntax,
                parsingError
            });
        }

        const propertyExpressionResolution = matchPropertyStackWithSchema(
            syntax, propertyNestings, { expressionType: 'Any' }, offset, buildSubExpressionResolver(this._entity, this._expressionResolversCallback));

        if (propertyExpressionResolution.isValid) {
            return successfulExpressionResolution(expression,
                new ScalarExpression(
                    ExpressionCategory.IterableItem,
                    `Foreach Item`,
                    expression,
                    `Foreach Item`,
                    (propertyExpressionResolution.expression as ScalarExpression).returnType,
                    (propertyExpressionResolution.expression as ScalarExpression).returnSubType));
        } else {
            return propertyExpressionResolution;
        }
    }

    public checkApplicability(entity: Owned): { isApplicable: boolean, error?: IResolutionError } {
        const isApplicable = this.isApplicable(entity);
        const resolutionError = isApplicable ? undefined : { kind: ResolutionErrorKind.ItemsSyntaxNotApplicable };

        return { isApplicable, error: resolutionError };
    }

    private getPrefix(ignoreStart: boolean): string {
        return ignoreStart ? 'item()' : '@item()';
    }

    private isApplicable(entity: Owned): boolean {
        // ensure that the field belongs to an activity which is under a for each activity or to a filter activity
        let isUnderForEachActivity: boolean =  false;
        let potentialForEachActivity = entity;
        while (potentialForEachActivity?.concept && entity.concept instanceof Pipeline && potentialForEachActivity.concept.owner) {
            if (Metamodel.modelOf(potentialForEachActivity.concept.owner) === ForEachActivity.MODEL) {
                isUnderForEachActivity = true;
                break;
            }
            potentialForEachActivity = potentialForEachActivity.concept.owner;
        }

        const isAFilterActivity = entity?.named && entity.named instanceof FilterActivity;

        return isUnderForEachActivity || isAFilterActivity;
    }
}
